<template>
  <p v-if="!applicantIncomes.length" class="kalp-subtle">Saknar underlag för att beräkna KALP.</p>

  <div v-else class="kalp-calculation">
    <div>
      <p class="kalp-version">Version: {{ kalpVersion ?? 'Saknas' }} · Alla belopp i tabellen är per månad</p>
      <AppTable>
        <thead>
          <tr>
            <th></th>
            <th v-for="im in applicantIncomes" :key="im.applicationUserId">
              {{ im.name }}
            </th>
            <th v-if="applicantIncomes.length > 1">Totalt</th>
          </tr>
        </thead>
        <tbody>
          <AppTableRow>
            <AppTableCell :colspan="String(columnCount)" class="section-heading">Inkomster</AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>Inkomst</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              {{ formatAmountWithCurrency(im.result.monthlyIncome) }}
              <div class="row-detail">({{ incomeSourceLabel(im.incomeSource) }})</div>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              {{ formatAmountWithCurrency(sumByKey('monthlyIncome')) }}
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>Barnbidrag</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              {{ im.result.childAllowance ? formatAmountWithCurrency(im.result.childAllowance) : '-' }}
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              {{ formatAmountWithCurrency(sumByKey('childAllowance')) }}
            </AppTableCell>
          </AppTableRow>
          <AppTableRow v-if="applicantIncomes.some((im) => im.result.monthlyCapitalIncome)">
            <AppTableCell>Inkomst kapital</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              {{ formatAmountWithCurrency(im.result.monthlyCapitalIncome ?? 0) }}
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              {{ formatAmountWithCurrency(sumByKey('monthlyCapitalIncome')) }}
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell :colspan="String(columnCount)" class="section-heading">Skatter och avgifter</AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>Kommunalskatt</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="localTaxMonthly(im.result) ? 'amount-negative' : ''">
                {{ localTaxMonthly(im.result) ? `- ${formatAmountWithCurrency(localTaxMonthly(im.result))}` : '-' }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumMonthly(localTaxMonthly) ? 'amount-negative' : ''">
                {{ sumMonthly(localTaxMonthly) ? `- ${formatAmountWithCurrency(sumMonthly(localTaxMonthly))}` : '-' }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>Statlig inkomstskatt</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="stateTaxMonthly(im.result) ? 'amount-negative' : ''">
                {{ stateTaxMonthly(im.result) ? `- ${formatAmountWithCurrency(stateTaxMonthly(im.result))}` : '-' }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumMonthly(stateTaxMonthly) ? 'amount-negative' : ''">
                {{ sumMonthly(stateTaxMonthly) ? `- ${formatAmountWithCurrency(sumMonthly(stateTaxMonthly))}` : '-' }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>Jobbskatteavdrag</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              {{
                earnedIncomeDeductionMonthly(im.result)
                  ? formatAmountWithCurrency(earnedIncomeDeductionMonthly(im.result))
                  : '-'
              }}
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              {{
                sumMonthly(earnedIncomeDeductionMonthly)
                  ? formatAmountWithCurrency(sumMonthly(earnedIncomeDeductionMonthly))
                  : '-'
              }}
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>Fastighetsavgift</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="propertyTaxMonthly(im.result) ? 'amount-negative' : ''">
                {{
                  propertyTaxMonthly(im.result) ? `- ${formatAmountWithCurrency(propertyTaxMonthly(im.result))}` : '-'
                }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumMonthly(propertyTaxMonthly) ? 'amount-negative' : ''">
                {{
                  sumMonthly(propertyTaxMonthly) ? `- ${formatAmountWithCurrency(sumMonthly(propertyTaxMonthly))}` : '-'
                }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>
              Övriga skatter och avgifter
              <div class="row-detail">(bl.a. begravningsavgift)</div>
            </AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="otherTaxesMonthly(im.result) ? 'amount-negative' : ''">
                {{ otherTaxesMonthly(im.result) ? `- ${formatAmountWithCurrency(otherTaxesMonthly(im.result))}` : '-' }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumMonthly(otherTaxesMonthly) ? 'amount-negative' : ''">
                {{
                  sumMonthly(otherTaxesMonthly) ? `- ${formatAmountWithCurrency(sumMonthly(otherTaxesMonthly))}` : '-'
                }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell class="total">Disponibel inkomst</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount total">
              = {{ formatAmountWithCurrency(im.result.disposableIncome) }}
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount total">
              = {{ formatAmountWithCurrency(kalp.disposableIncome) }}
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell :colspan="String(columnCount)" class="section-heading">Kostnader</AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>
              Räntekostnad för andra lån
              <div class="row-detail">
                <span v-for="(part, i) in loanBalancesDetail" :key="part">
                  {{ i ? ' · ' : '' }}<span class="detail-part">{{ part }}</span>
                </span>
              </div>
            </AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="im.result.interestExpenseTotal ? 'amount-negative' : ''">
                {{
                  im.result.interestExpenseTotal ? `- ${formatAmountWithCurrency(im.result.interestExpenseTotal)}` : '-'
                }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumByKey('interestExpenseTotal') ? 'amount-negative' : ''">
                {{
                  sumByKey('interestExpenseTotal')
                    ? `- ${formatAmountWithCurrency(sumByKey('interestExpenseTotal'))}`
                    : '-'
                }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>
              Amortering för andra lån
              <div class="row-detail">
                Bolån på övriga boenden som behålls, billån, kortkredit och privat/övrigt lån
              </div>
            </AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="im.result.amortizationTotal ? 'amount-negative' : ''">
                {{ im.result.amortizationTotal ? `- ${formatAmountWithCurrency(im.result.amortizationTotal)}` : '-' }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumByKey('amortizationTotal') ? 'amount-negative' : ''">
                {{
                  sumByKey('amortizationTotal') ? `- ${formatAmountWithCurrency(sumByKey('amortizationTotal'))}` : '-'
                }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>Studielån</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="im.result.studentLoanAmounts ? 'amount-negative' : ''">
                {{ im.result.studentLoanAmounts ? `- ${formatAmountWithCurrency(im.result.studentLoanAmounts)}` : '-' }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumByKey('studentLoanAmounts') ? 'amount-negative' : ''">
                {{
                  sumByKey('studentLoanAmounts') ? `- ${formatAmountWithCurrency(sumByKey('studentLoanAmounts'))}` : '-'
                }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>
              Kostnad för övriga boenden
              <div class="row-detail">{{ details.otherProperties }}</div>
            </AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="im.result.otherPropertiesMonthlyFee ? 'amount-negative' : ''">
                {{
                  im.result.otherPropertiesMonthlyFee
                    ? `- ${formatAmountWithCurrency(im.result.otherPropertiesMonthlyFee)}`
                    : '-'
                }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumByKey('otherPropertiesMonthlyFee') ? 'amount-negative' : ''">
                {{
                  sumByKey('otherPropertiesMonthlyFee')
                    ? `- ${formatAmountWithCurrency(sumByKey('otherPropertiesMonthlyFee'))}`
                    : '-'
                }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow v-if="applicantIncomes.some((im) => im.result.carCostMonthly != null)">
            <AppTableCell>Bilkostnad</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span
                :class="im.result.carCostMonthly != null && im.result.carCostMonthly !== 0 ? 'amount-negative' : ''"
              >
                {{ im.result.carCostMonthly != null ? `- ${formatAmountWithCurrency(im.result.carCostMonthly)}` : '-' }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumByKey('carCostMonthly') ? 'amount-negative' : ''">
                {{ sumByKey('carCostMonthly') ? `- ${formatAmountWithCurrency(sumByKey('carCostMonthly'))}` : '-' }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>
              Levnadskostnader för hushållet
              <div class="row-detail">{{ details.household }}</div>
            </AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="im.result.houseHoldLivingCost ? 'amount-negative' : ''">
                {{
                  im.result.houseHoldLivingCost ? `- ${formatAmountWithCurrency(im.result.houseHoldLivingCost)}` : '-'
                }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumByKey('houseHoldLivingCost') ? 'amount-negative' : ''">
                {{
                  sumByKey('houseHoldLivingCost')
                    ? `- ${formatAmountWithCurrency(sumByKey('houseHoldLivingCost'))}`
                    : '-'
                }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell>
              Kostnad för bostaden som belånas
              <div class="row-detail">{{ details.collateralProperty }}</div>
            </AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount">
              <span :class="im.result.propertyMonthlyFee ? 'amount-negative' : ''">
                {{ im.result.propertyMonthlyFee ? `- ${formatAmountWithCurrency(im.result.propertyMonthlyFee)}` : '-' }}
              </span>
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount">
              <span :class="sumByKey('propertyMonthlyFee') ? 'amount-negative' : ''">
                {{
                  sumByKey('propertyMonthlyFee') ? `- ${formatAmountWithCurrency(sumByKey('propertyMonthlyFee'))}` : '-'
                }}
              </span>
            </AppTableCell>
          </AppTableRow>
          <AppTableRow>
            <AppTableCell class="total">Kvar att leva på (exkl. ansökta lånets kostnader)</AppTableCell>
            <AppTableCell v-for="im in applicantIncomes" :key="im.applicationUserId" class="amount total">
              = {{ formatAmountWithCurrency(im.result.houseHoldAmount) }}
            </AppTableCell>
            <AppTableCell v-if="applicantIncomes.length > 1" class="amount total">
              = {{ formatAmountWithCurrency(kalp.houseHoldAmount) }}
            </AppTableCell>
          </AppTableRow>
          <!-- The applied loan is the household's, so its rows have one value spanning the applicant columns -->
          <template
            v-if="
              kalp.houseHoldAmountAtAppliedLoan != null &&
              kalp.interestExpenseAtAppliedLoan != null &&
              kalp.amortizationAtAppliedLoan != null
            "
          >
            <AppTableRow>
              <AppTableCell :colspan="String(columnCount)" class="section-heading">Ansökt bolån</AppTableCell>
            </AppTableRow>
            <AppTableRow>
              <AppTableCell>
                Räntekostnad för sökt lån
                <div class="row-detail">{{ appliedLoanDetail }}</div>
              </AppTableCell>
              <AppTableCell :colspan="String(columnCount - 1)" class="amount">
                <span :class="kalp.interestExpenseAtAppliedLoan ? 'amount-negative' : ''">
                  {{
                    kalp.interestExpenseAtAppliedLoan
                      ? `- ${formatAmountWithCurrency(kalp.interestExpenseAtAppliedLoan)}`
                      : '-'
                  }}
                </span>
              </AppTableCell>
            </AppTableRow>
            <AppTableRow>
              <AppTableCell>Amortering för sökt lån</AppTableCell>
              <AppTableCell :colspan="String(columnCount - 1)" class="amount">
                <span :class="kalp.amortizationAtAppliedLoan ? 'amount-negative' : ''">
                  {{
                    kalp.amortizationAtAppliedLoan
                      ? `- ${formatAmountWithCurrency(kalp.amortizationAtAppliedLoan)}`
                      : '-'
                  }}
                </span>
              </AppTableCell>
            </AppTableRow>
            <AppTableRow>
              <AppTableCell class="total">Kvar att leva på (inkl. ansökta lånets kostnader)</AppTableCell>
              <AppTableCell :colspan="String(columnCount - 1)" class="amount total">
                = {{ formatAmountWithCurrency(kalp.houseHoldAmountAtAppliedLoan) }}
              </AppTableCell>
            </AppTableRow>
          </template>
        </tbody>
      </AppTable>
    </div>

    <div>
      <p class="max-loan">
        Högsta lånebelopp hushållet kan bära enligt KALP-beräkningen: {{ formatAmountWithCurrency(kalp.maxLoanKALP) }}
      </p>
      <p class="max-loan-detail">{{ maxLoanDetail }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { formatAmountWithCurrency } from '@staff/shared'
import AppTable from '../AppTable/AppTable.vue'
import AppTableRow from '../AppTable/AppTableRow.vue'
import AppTableCell from '../AppTable/AppTableCell.vue'
import type {
  KalpCalculationData,
  KalpCalculationDetails,
  KalpCalculationIncomeModel,
  KalpCalculationResult,
} from './KalpCalculation.types'

const props = defineProps<{
  kalp: KalpCalculationData
  details: KalpCalculationDetails
  isTopupLoan: boolean
}>()

const kalp = computed(() => props.kalp)

const applicantIncomes = computed(() => kalp.value.incomeModels)

const columnCount = computed(() => 1 + applicantIncomes.value.length + (applicantIncomes.value.length > 1 ? 1 : 0))

// The tax components in the response are yearly amounts; the table shows monthly figures.
// Every field is guarded with ?? 0 so responses from a backend without the tax breakdown
// degrade to showing the whole tax as "Övriga skatter" instead of NaN-blanking the rows.
const localTaxMonthly = (result: KalpCalculationResult) => Math.round((result.localIncomeTax ?? 0) / 12)
const stateTaxMonthly = (result: KalpCalculationResult) => Math.round((result.stateIncomeTax ?? 0) / 12)
const earnedIncomeDeductionMonthly = (result: KalpCalculationResult) =>
  Math.round((result.earnedIncomeDeduction ?? 0) / 12)
const propertyTaxMonthly = (result: KalpCalculationResult) =>
  Math.round(((result.propertyTaxationFee ?? 0) + (result.otherPropertiesTaxationFee ?? 0)) / 12)

// The part of the monthly tax total that the components above don't explain (the funeral fee).
// Rows are rounded individually, so the section may be off by a krona against the disposable
// income — that rounding difference is deliberately NOT smoothed over.
const otherTaxesMonthly = (result: KalpCalculationResult) => {
  const explained =
    ((result.localIncomeTax ?? 0) +
      (result.stateIncomeTax ?? 0) -
      (result.earnedIncomeDeduction ?? 0) +
      (result.propertyTaxationFee ?? 0) +
      (result.otherPropertiesTaxationFee ?? 0)) /
    12
  return Math.max(Math.round(result.totalTaxMonthly - explained), 0)
}

const sumMonthly = (figure: (result: KalpCalculationResult) => number) =>
  applicantIncomes.value.reduce((sum, im) => sum + figure(im.result), 0)

const maxLoanDetail = computed(() => {
  const loanText = `Vid lån på ${formatAmountWithCurrency(kalp.value.maxLoanKALP)}`
  const rate = applicantIncomes.value[0]?.result.interestRateApplication
  const rateText = rate != null ? ` och kalkylräntan ${(rate * 100).toLocaleString('sv-SE')} %` : ''
  const amortization = kalp.value.amortizationAtMaxLoanKALP

  if (amortization != null) {
    const percentage = kalp.value.calculatedAmortizationPercentage
    const percentageText = percentage != null ? ` (${(percentage * 100).toLocaleString('sv-SE')} %)` : ''
    return `${loanText}${rateText} behöver kund amortera ${formatAmountWithCurrency(amortization)}${percentageText} och kvar att leva på är 0 kr.`
  }
  return `${loanText}${rateText} är kvar att leva på 0 kr.`
})

// The applied loan includes the existing debt for a topup, so the amount can differ from the amount
// applied for shown elsewhere in the application.
const appliedLoanDetail = computed(() => {
  if (kalp.value.appliedLoanAmount == null) {
    return ''
  }

  const loanText = `Lån ${formatAmountWithCurrency(kalp.value.appliedLoanAmount)}`
  const topupText = props.isTopupLoan ? ' (befintlig skuld + sökt tillägg)' : ''
  const rate = applicantIncomes.value[0]?.result.interestRateApplication
  const rateText = rate != null ? ` · kalkylränta ${(rate * 100).toLocaleString('sv-SE')} %` : ''
  return `${loanText}${topupText}${rateText} · efter ränteavdrag`
})

const incomeSourceLabel = (incomeSource: KalpCalculationIncomeModel['incomeSource']) => {
  switch (incomeSource) {
    case 'stated':
      return 'uppgiven inkomst'
    case 'uc':
      return 'inkomst från UC'
    case 'complement':
      return 'inkomst från komplettering'
    case null:
      return 'okänd inkomstkälla'
  }
}

const kalpVersion = computed(() => applicantIncomes.value.find((im) => im.result.kalpVersion)?.result.kalpVersion)

const sumByKey = (key: keyof KalpCalculationResult): number => {
  return applicantIncomes.value.reduce((sum, { result }) => {
    const value = result[key]
    return typeof value === 'number' ? sum + value : sum
  }, 0)
}

// Each part stays on one line, so an amount is never split over two lines
const loanBalancesDetail = computed(() => [
  'Bolån på övriga boenden som behålls',
  `Billån ${formatAmountWithCurrency(sumByKey('carLoanAmounts'))}`,
  `Kortkredit ${formatAmountWithCurrency(sumByKey('creditCardLoanAmounts'))}`,
  `Privat/övrigt lån ${formatAmountWithCurrency(sumByKey('otherLoanAmounts'))}`,
])
</script>

<style lang="scss" scoped>
.kalp-calculation {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.kalp-subtle,
.kalp-version,
.max-loan-detail {
  color: var(--color-fg-subtle);
}

.kalp-version {
  margin-bottom: 0.5rem;
  font-size: 0.75rem;
}

.max-loan {
  font-size: 1.125rem;
  font-weight: 600;
}

.max-loan-detail {
  font-size: 0.875rem;
}

:deep(table) {
  table-layout: fixed;
}

:deep(th:first-child),
:deep(td:first-child) {
  width: 40%;
}

:deep(th:not(:first-child)) {
  text-align: right !important;
}

.amount {
  text-align: right;
  white-space: nowrap;
}

.total {
  font-weight: 500;
}

.detail-part {
  white-space: nowrap;
}

.row-detail {
  font-size: 0.75rem;
  font-weight: normal;
  color: var(--color-fg-subtle);
}

.section-heading {
  font-weight: 600;
  background-color: var(--color-canvas-subtle);
}

.amount-negative {
  color: var(--color-danger-fg);
}
</style>
